import { geoDistance } from "d3-geo";

const CX = 550;
const CY = 350;
const PIN_BASE_DELAY = 1.8;
const PIN_STEP = 0.18;
const ARC_BASE_DELAY = 3.6;
const ARC_STEP = 1.4;

function defaultLabel(loc, projection) {
    const xy = projection(loc.coords) || [CX, CY];
    const onLeftHalf = xy[0] < CX;
    const onUpperHalf = xy[1] < CY;
    return {
        lx: onLeftHalf ? -42 : 42,
        ly: onUpperHalf ? 28 : -22,
        anchor: onLeftHalf ? "end" : "start",
    };
}

export function renderPins(gPins, locations, projection) {
    return locations.map((loc, i) => {
        const label = loc.label || defaultLabel(loc, projection);
        const g = gPins
            .append("g")
            .attr("class", "world-map__pin")
            .style("--pin-delay", `${PIN_BASE_DELAY + i * PIN_STEP}s`);

        const ex = Math.sign(label.lx) * 8;
        const ey = label.ly * 0.5;
        g.append("path")
            .attr("class", "world-map__leader")
            .attr("d", `M0,0 L${ex},${ey} L${label.lx},${label.ly}`);

        const m = g.append("g").attr("class", "world-map__marker");
        if (loc.soon) {
            m.append("circle")
                .attr("class", "world-map__pin-soon")
                .attr("r", 6);
        } else {
            m.append("circle")
                .attr("class", "world-map__pin-halo")
                .attr("r", 5)
                .style("animation-delay", `${-0.4 * i}s`);
            m.append("circle")
                .attr("class", "world-map__pin-ring")
                .attr("r", 8);
            m.append("circle")
                .attr("class", "world-map__pin-dot")
                .attr("r", 3.5);
        }

        g.append("circle").attr("class", "world-map__pin-hit").attr("r", 18);

        const labelX = label.lx + Math.sign(label.lx) * 4;
        if (loc.soon) {
            g.append("text")
                .attr("class", "world-map__label")
                .attr("x", labelX)
                .attr("y", label.ly - 2)
                .attr("text-anchor", label.anchor)
                .text(loc.name);
            g.append("text")
                .attr("class", "world-map__label-soon")
                .attr("x", labelX)
                .attr("y", label.ly + 12)
                .attr("text-anchor", label.anchor)
                .text("COMING SOON");
        } else {
            g.append("text")
                .attr("class", "world-map__label")
                .attr("x", labelX)
                .attr("y", label.ly + 4)
                .attr("text-anchor", label.anchor)
                .text(loc.short || loc.name);
        }

        return { el: g.node(), coords: loc.coords };
    });
}

export function updatePins(pins, projection) {
    const r = projection.rotate();
    const center = [-r[0], -r[1]];
    const limit = Math.PI / 2 - 0.02;
    pins.forEach((p) => {
        const xy = projection(p.coords);
        const visible = xy && geoDistance(center, p.coords) < limit;
        p.el.style.display = visible ? "" : "none";
        if (visible) {
            p.el.setAttribute("transform", `translate(${xy[0]},${xy[1]})`);
        }
    });
}

export function renderArcs(gArcs, arcs, locations) {
    const byName = Object.fromEntries(locations.map((l) => [l.name, l]));
    return arcs
        .map((pair, i) => {
            const [aName, bName] = pair;
            const A = byName[aName];
            const B = byName[bName];
            if (!A || !B) return null;
            const sel = gArcs
                .append("path")
                .attr("class", "world-map__arc")
                .style("animation-delay", `${ARC_BASE_DELAY + i * ARC_STEP}s`);
            return { sel, pair: [A.coords, B.coords] };
        })
        .filter(Boolean);
}

export function updateArcs(arcs, path) {
    arcs.forEach((rec) => {
        const d = path({ type: "LineString", coordinates: rec.pair });
        rec.sel.attr("d", d || "");
        if (d) {
            const len = rec.sel.node().getTotalLength();
            rec.sel
                .style("--len", `${len}px`)
                .style("--neg-len", `${-len}px`);
        }
    });
}
